import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Upload, Image, Loader2, CheckCircle, AlertCircle, Camera, FileText, Users, Tag, Settings, Zap, X, Hash } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Event {
  id: string;
  name: string;
  date: string;
  location: string;
}

interface ImageMetadata {
  originalName: string;
  technicalData: {
    camera?: string;
    resolution?: string;
    quality?: string;
    iso?: string;
    aperture?: string;
    shutterSpeed?: string;
    focalLength?: string;
    dateTime?: string;
    gps?: string;
  };
  fileInfo: {
    size: number;
    type: string;
    lastModified: number;
  };
  suggestedAttributes: {
    sport?: string;
    timeOfDay?: string;
    weather?: string;
    location?: string;
  };
  bibNumber?: string;
  processingNotes: string[];
}

interface UploadFile {
  id: string;
  file: File;
  preview: string;
  status: 'pending' | 'processing' | 'processed' | 'uploaded' | 'error';
  progress: number;
  bibNumber?: string;
  athleteIds?: string;
  metadata?: ImageMetadata;
  processingNotes?: string[];
}

export default function FormUploader() {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const { toast } = useToast();

  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Buscar eventos disponíveis
  useEffect(() => {
    const fetchEvents = async () => {
      setEventsLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/events`);
        if (response.ok) {
          const data = await response.json();
          setEvents(data.events || []);
        }
      } catch (error) {
        console.error('Erro ao buscar eventos:', error);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      status: 'pending' as const,
      progress: 0,
      bibNumber: '',
      athleteIds: '',
      metadata: {
        originalName: file.name,
        technicalData: {},
        bibNumber: '',
        athleteIds: '',
        tags: [],
        description: ''
      },
      processingNotes: []
    }));

    setFiles(prev => [...prev, ...newFiles as any]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true
  });

  const removeFile = (id: string) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== id);
    });
    if (selectedFileId === id) {
      setSelectedFileId(null);
    }
  };

  const updateFileMetadata = (fileId: string, metadata: Partial<ImageMetadata>) => {
    setFiles(prev => prev.map(file => 
      file.id === fileId 
        ? { ...file, metadata: { ...file.metadata, ...metadata } as any }
        : file
    ));
  };

  const processImage = async (file: UploadFile) => {
    try {
      // Simular processamento de imagem para extrair metadados
      // Em produção, isso seria feito por um serviço de processamento de imagens
      
      const img = new (window as any).Image();
      img.src = file.preview;
      
      return new Promise<ImageMetadata>((resolve) => {
        img.onload = () => {
          // Extrair dados básicos da imagem
          const technicalData = {
            resolution: `${img.naturalWidth}x${img.naturalHeight}`,
            quality: 'Alta',
            dateTime: new Date().toISOString(),
            // Simular outros dados técnicos
            camera: 'Canon EOS R5', // Seria extraído dos metadados EXIF
            iso: '400',
            aperture: 'f/2.8',
            shutterSpeed: '1/250s',
            focalLength: '85mm'
          };

          // Simular detecção de número do peito (futuramente com IA/OCR)
          const possibleBibNumber = extractBibNumberFromFilename(file.file.name);

          resolve({
            ...file.metadata,
            technicalData,
            bibNumber: possibleBibNumber
          } as any);
        };
      });
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      throw error;
    }
  };

  const extractBibNumberFromFilename = (filename: string): string => {
    // Tentar extrair número do peito do nome do arquivo
    const match = filename.match(/\d{3,5}/);
    return match ? match[0] : '';
  };

  const processAllImages = async () => {
    if (files.length === 0) {
      toast({
        title: "Erro",
        description: "Selecione pelo menos uma foto para processar",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('photos', file.file);
      });

      console.log('🔄 Processando imagens...', { count: files.length });

      const response = await fetch(`${API_BASE}/api/photos/process`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Atualizar os metadados dos arquivos com os dados processados
        const updatedFiles = files.map((file, index) => {
          const processedData = result.data.processedImages[index];
          if (processedData) {
            return {
              ...file,
              status: 'processed',
              metadata: processedData.metadata,
              bibNumber: processedData.bibNumber || '',
              athleteIds: processedData.athleteIds?.join(', ') || '',
              processingNotes: processedData.processingNotes || []
            };
          }
          return file;
        });

        setFiles(updatedFiles as any);
        
        toast({
          title: "Processamento Concluído",
          description: `${result.data.totalProcessed} imagens processadas com sucesso`,
        });

        console.log('✅ Processamento concluído:', result.data);
      } else {
        throw new Error(result.message || 'Erro no processamento');
      }
    } catch (error) {
      console.error('❌ Erro no processamento:', error);
      toast({
        title: "Erro no Processamento",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpload = async () => {
    const processedFiles = files.filter(f => f.status === 'processed');
    
    if (processedFiles.length === 0) {
      toast({
        title: "Erro",
        description: "Processe as imagens antes de enviar",
        variant: "destructive",
      });
      return;
    }

    if (!selectedEventId) {
      toast({
        title: "Erro",
        description: "Selecione um evento",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      
      // Adicionar arquivos
      processedFiles.forEach((file) => {
        formData.append('photos', file.file);
      });

      // Adicionar metadados do formulário
      formData.append('eventId', selectedEventId);
      formData.append('category', category);
      formData.append('description', description);
      
      // Adicionar metadados processados de cada arquivo
       const filesMetadata = processedFiles.map((file, index) => ({
         originalName: file.metadata?.originalName || file.file.name,
         bibNumber: file.bibNumber || '',
         athleteIds: file.athleteIds || '',
         technicalData: file.metadata?.technicalData || {},
         suggestedAttributes: file.metadata?.suggestedAttributes || {},
         processingNotes: file.processingNotes || []
       }));
      
      formData.append('filesMetadata', JSON.stringify(filesMetadata));

      console.log('📤 Enviando fotos processadas...', {
        eventId: selectedEventId,
        filesCount: processedFiles.length
      });

      // Simular progresso de upload
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 200);

      const response = await fetch(`${API_BASE}/api/photos/upload`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Marcar arquivos como enviados
         setFiles(prev => prev.map(file => 
           processedFiles.some(pf => pf.id === file.id) 
             ? { ...file, status: 'uploaded' }
             : file
         ));

        toast({
          title: "Upload Concluído",
          description: `${processedFiles.length} fotos enviadas com sucesso!`,
        });

        console.log('✅ Upload concluído:', result);

        // Limpar formulário após sucesso
        setTimeout(() => {
          setFiles([]);
          setDescription('');
          setCategory('');
          setUploadProgress(0);
        }, 2000);

      } else {
        throw new Error(result.message || 'Erro no upload');
      }

    } catch (error) {
      console.error('❌ Erro no upload:', error);
      toast({
        title: "Erro no Upload",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const selectedFile = selectedFileId ? files.find(f => f.id === selectedFileId) : null;

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Upload Avançado de Fotos</h1>
        <p className="text-muted-foreground mt-2">
          Selecione, processe e envie suas fotos com metadados automáticos
        </p>
      </div>

      {/* Seleção de Evento */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configurações do Upload
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="event">Evento *</Label>
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um evento" />
              </SelectTrigger>
              <SelectContent>
                {eventsLoading ? (
                  <SelectItem value="loading" disabled>Carregando eventos...</SelectItem>
                ) : (
                  events.map((event: Event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} - {new Date(event.date).toLocaleDateString()}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="category">Categoria</Label>
            <Input
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Ex: Corrida, Chegada, Largada..."
            />
          </div>

          <div>
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição adicional sobre as fotos..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Área de Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Selecionar Fotos
          </CardTitle>
          <CardDescription>
            Arraste e solte múltiplas fotos aqui ou clique para selecionar. Máximo 10MB por foto.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-primary bg-primary/5' 
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            {isDragActive ? (
              <p className="text-lg font-medium">Solte as fotos aqui...</p>
            ) : (
              <div>
                <p className="text-lg font-medium mb-2">Clique ou arraste fotos para cá</p>
                <p className="text-sm text-muted-foreground">
                  Suporta: JPEG, PNG, WebP (máx. 10MB cada)
                </p>
              </div>
            )}
          </div>

          {files.length > 0 && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Fotos Selecionadas ({files.length})
                </h3>
                <Button 
                  onClick={processAllImages}
                  disabled={isProcessing || files.every(f => f.status === 'processed')}
                  className="flex items-center gap-2"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4" />
                  )}
                  Processar Todas
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {files.map((uploadFile) => (
                  <div 
                    key={uploadFile.id} 
                    className={`relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-colors ${
                      selectedFileId === uploadFile.id 
                        ? 'border-primary' 
                        : 'border-transparent hover:border-muted-foreground/25'
                    }`}
                    onClick={() => setSelectedFileId(uploadFile.id)}
                  >
                    <div className="aspect-square bg-muted">
                      <img
                        src={uploadFile.preview}
                        alt={uploadFile.file.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="absolute top-2 right-2 flex gap-1">
                      {uploadFile.status === 'processing' && (
                        <Badge variant="secondary" className="text-xs">
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          Processando
                        </Badge>
                      )}
                      {uploadFile.status === 'processed' && (
                        <Badge variant="default" className="text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Processado
                        </Badge>
                      )}
                      {uploadFile.status === 'uploaded' && (
                        <Badge variant="default" className="text-xs bg-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Enviado
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(uploadFile.id);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 bg-black/75 text-white p-2">
                      <p className="text-xs truncate">{uploadFile.file.name}</p>
                      {uploadFile.metadata?.bibNumber && (
                        <p className="text-xs text-green-300">
                          Peito: {uploadFile.metadata.bibNumber}
                        </p>
                      )}
                      {uploadFile.metadata && uploadFile.metadata.technicalData && (
                        <div className="mt-2 space-y-1">
                          <div className="text-xs">
                            <div className="grid grid-cols-2 gap-1 text-xs">
                              {uploadFile.metadata.technicalData.camera && (
                                <div><span className="font-medium">Câmera:</span> {uploadFile.metadata.technicalData.camera}</div>
                              )}
                              {uploadFile.metadata.technicalData.resolution && (
                                <div><span className="font-medium">Resolução:</span> {uploadFile.metadata.technicalData.resolution}</div>
                              )}
                              {uploadFile.metadata.technicalData.iso && (
                                <div><span className="font-medium">ISO:</span> {uploadFile.metadata.technicalData.iso}</div>
                              )}
                              {uploadFile.metadata.technicalData.aperture && (
                                <div><span className="font-medium">Abertura:</span> {uploadFile.metadata.technicalData.aperture}</div>
                              )}
                              {uploadFile.metadata.technicalData.shutterSpeed && (
                                <div><span className="font-medium">Velocidade:</span> {uploadFile.metadata.technicalData.shutterSpeed}</div>
                              )}
                              {uploadFile.metadata.technicalData.focalLength && (
                                <div><span className="font-medium">Focal:</span> {uploadFile.metadata.technicalData.focalLength}</div>
                              )}
                            </div>
                          </div>
                          
                          {uploadFile.processingNotes && uploadFile.processingNotes.length > 0 && (
                            <div className="text-xs">
                              <div className="font-medium mb-1">Notas:</div>
                              <ul className="text-xs space-y-1">
                                {uploadFile.processingNotes.map((note, index) => (
                                  <li key={index} className="flex items-center gap-1">
                                    <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                                    {note}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Painel de Metadados */}
      {selectedFile && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Metadados da Foto
            </CardTitle>
            <CardDescription>
              {selectedFile.file.name}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Nome Original */}
            <div>
              <Label htmlFor="originalName">Nome Original do Arquivo</Label>
              <Input
                id="originalName"
                value={selectedFile.metadata?.originalName || ''}
                onChange={(e) => updateFileMetadata(selectedFile.id, { 
                  originalName: e.target.value 
                })}
              />
            </div>

            {/* Dados Técnicos */}
            <div>
              <Label className="flex items-center gap-2 mb-3">
                <Camera className="h-4 w-4" />
                Dados Técnicos da Imagem
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="camera">Câmera</Label>
                  <Input
                    id="camera"
                    value={selectedFile.metadata?.technicalData?.camera || ''}
                    onChange={(e) => updateFileMetadata(selectedFile.id, {
                      technicalData: {
                        ...selectedFile.metadata?.technicalData,
                        camera: e.target.value
                      }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="resolution">Resolução</Label>
                  <Input
                    id="resolution"
                    value={selectedFile.metadata?.technicalData?.resolution || ''}
                    onChange={(e) => updateFileMetadata(selectedFile.id, {
                      technicalData: {
                        ...selectedFile.metadata?.technicalData,
                        resolution: e.target.value
                      }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="iso">ISO</Label>
                  <Input
                    id="iso"
                    value={selectedFile.metadata?.technicalData?.iso || ''}
                    onChange={(e) => updateFileMetadata(selectedFile.id, {
                      technicalData: {
                        ...selectedFile.metadata?.technicalData,
                        iso: e.target.value
                      }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="aperture">Abertura</Label>
                  <Input
                    id="aperture"
                    value={selectedFile.metadata?.technicalData?.aperture || ''}
                    onChange={(e) => updateFileMetadata(selectedFile.id, {
                      technicalData: {
                        ...selectedFile.metadata?.technicalData,
                        aperture: e.target.value
                      }
                    })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Número do Peito */}
            <div>
              <Label htmlFor="bibNumber" className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Número do Peito (Bib)
              </Label>
              <Input
                id="bibNumber"
                value={selectedFile.metadata?.bibNumber || ''}
                onChange={(e) => updateFileMetadata(selectedFile.id, { 
                  bibNumber: e.target.value 
                })}
                placeholder="Ex: 1234"
              />
            </div>

            {/* IDs dos Atletas */}
            <div>
              <Label htmlFor="athleteIds" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                IDs dos Atletas Identificados
              </Label>
              <Input
                id="athleteIds"
                value={(selectedFile.metadata as any)?.athleteIds || ''}
                onChange={(e) => updateFileMetadata(selectedFile.id, { 
                  athleteIds: e.target.value 
                } as any)}
                placeholder="Ex: 123, 456, 789"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Separe múltiplos IDs por vírgula
              </p>
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={(selectedFile.metadata as any)?.description || ''}
                onChange={(e) => updateFileMetadata(selectedFile.id, { 
                  description: e.target.value 
                } as any)}
                placeholder="Adicione uma descrição para esta foto..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Botão de Upload */}
      {files.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            {isUploading && (
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Enviando fotos...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}
            
            <Button 
              onClick={handleUpload}
              disabled={isUploading || !selectedEventId || files.length === 0}
              className="w-full"
              size="lg"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Enviar Fotos ({files.filter(f => f.status === 'processed').length})
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}